import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { VerticalMenu } from '@/components/shared/menu/VerticalMenu';
import { convertLeaveTypeFromGql } from '@/services/leaveManagement/leaveManagementHelper';
import { useGetLeaveType } from '@/services/leaveManagement/LeaveTypesProvider';
import { FC } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { IoClose, IoRefresh } from 'react-icons/io5';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import strings from '../../admin.translations';
import AdminDeactivateReactivateTaskCard from './AdminDeactivateReactivateLeaveTypeCard';
import AdminEditLeaveTypeDetailsCard from './AdminEditLeaveTypeDetailsCard';

const deactivateReactivateConfig = [
  { icon: IoRefresh, label: strings.common.reactivate }, // false = inactive
  { icon: IoClose, label: strings.common.deactivate } // true = active
] as const;

export const AdminEditLeaveTypePage: FC = () => {
  const { tabName, leaveTypeId } = useParams();

  if (!leaveTypeId) throw new Error('Missing leave type id');

  const { state } = useLocation();

  const navigate = useNavigate();

  const getLeaveType = useGetLeaveType();

  const leaveType = state?.leaveType ? convertLeaveTypeFromGql(state.leaveType) : getLeaveType(leaveTypeId);

  if (!leaveType) {
    throw new Error(`Leave type with ID ${leaveTypeId} not found`);
  }

  const breadcrumbs: NavPage[] = [
    {
      name: strings.page.admin,
      to: '/admin'
    },
    {
      name: strings.leaveTypeSection.edit.leaveType,
      to: '/admin/leave-types'
    },
    {
      name: leaveType.name,
      to: `/admin/leave-types/${leaveTypeId}`
    }
  ];

  const { icon: DeactivateReactivateIcon, label: deactivateReactivateLabel } =
    deactivateReactivateConfig[+leaveType.active];

  const sideMenuOptions = [
    {
      icon: <FiCalendar className="h-5 w-5" />,
      label: strings.leaveTypeSection.edit.leaveTypeDetails,
      anchorId: 'edit-leave-type',
      element: <AdminEditLeaveTypeDetailsCard leaveType={leaveType} />
    },
    {
      icon: <DeactivateReactivateIcon className="h-5 w-5" />,
      label: deactivateReactivateLabel,
      anchorId: 'deactivate-reactivate',
      element: <AdminDeactivateReactivateTaskCard leaveType={leaveType} />
    }
  ];

  const allElements = (
    <>
      <AdminEditLeaveTypeDetailsCard leaveType={leaveType} />
      <AdminDeactivateReactivateTaskCard leaveType={leaveType} />
    </>
  );

  const selectedTab = tabName ?? sideMenuOptions[0].anchorId;
  const handleTabSelect = (anchorId: string) => {
    navigate(`/admin/leave-types/${leaveTypeId}/${anchorId}`);
  };

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.page.title} icon={<FiCalendar />} breadcrumbs={breadcrumbs} />}
    >
      <PageSizer className="flex gap-5 flex-col lg:flex-row" mode={SizerMode.PAGE}>
        <div className="hidden lg:flex gap-5 w-full">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            isScroll={false}
            selectedTab={selectedTab}
          />
        </div>
        <div className="lg:hidden">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            allElements={allElements}
            isScroll={true}
            selectedTab={selectedTab}
          />
        </div>
      </PageSizer>
    </PageContainer>
  );
};
